/* eslint-disable no-underscore-dangle */
import dayjs from 'dayjs';
import * as logger from 'loglevel';

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
];
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const labelCommonKey = 'cmp-locale-day-js-';

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Given a month name gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "Enero"
 * else => labels["month-january"]
 * @param {*} months
 * @param {*} index
 * @param {*} translate
 * @param {*} labelKey
 * @param {*} defaultValue
 */
const getMonthTranslation = (months, index, translate, labelKey, defaultValue) => {
  const _default = months[index]
    ? months[index].charAt(0).toUpperCase() + months[index].substring(1)
    : defaultValue;
  const translated = translate(labelKey, _default);
  if (!translated) {
    return _default;
  }
  return translated;
};

/**
 * Given a weekday array gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "Do" (Domingo)
 * else => labels["day-min-sunday"]
 * @param {*} entity
 * @param {*} index
 * @param {*} translate
 * @param {*} labelKey
 * @param {*} defaultValue
 */
const getWeekDayMinTranslation = (entity, index, translate, labelKey, defaultValue) => {
  const _default = entity[index]
    ? entity[index].charAt(0).toUpperCase() + entity[index].substring(1)
    : defaultValue;
  const translated = translate(labelKey, _default);
  if (!translated) {
    return _default;
  }
  return translated;
};

/**
 * Given a weekday array gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "D" (Domingo)
 * else => labels["day-one-letter-sunday"]
 * @param {*} entity
 * @param {*} index
 * @param {*} translate
 * @param {*} labelKey
 * @param {*} defaultValue
 */
const getWeekDayTranslation = (entity, index, translate, labelKey, defaultValue) => {
  const _default = entity[index] ? entity[index].charAt(0).toUpperCase() : defaultValue;
  const translated = translate(labelKey, _default);
  if (!translated) {
    return _default;
  }
  return translated;
};

/**
 * Given a weekday array gets the localization name
 * in short format if is defined or a label translation for it
 * if moment["ES"] => return "Domingo" (Domingo)
 * else => labels["day-short-sunday"]
 * @param {*} entity
 * @param {*} index
 * @param {*} translate
 * @param {*} labelKey
 * @param {*} defaultValue
 */
const getWeekDaysShortTranslation = (entity, index, translate, labelKey, defaultValue) => {
  const _default = entity[index]
    ? entity[index].charAt(0).toUpperCase() + entity[index].substring(1)
    : defaultValue;
  const translated = translate(labelKey, _default);
  if (!translated) {
    return _default;
  }
  return translated;
};

/**
 * Given a weekday array gets the localization name
 * in short format if is defined or a label translation for it
 * if moment["ES"] => return "Domingo" (Domingo)
 * else => labels["day-long-sunday"]
 * @param {*} entity
 * @param {*} index
 * @param {*} translate
 * @param {*} labelKey
 * @param {*} defaultValue
 */
const getWeekDaysLongTranslation = (entity, index, translate, labelKey, defaultValue) => {
  const _default = entity[index]
    ? entity[index].charAt(0).toUpperCase() + entity[index].substring(1)
    : defaultValue;
  const translated = translate(labelKey, _default);
  if (!translated) {
    return _default;
  }
  return translated;
};
/**
 * Given a month name gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "Enero"
 * else => labels["month-january"]
 * @param {*} translate
 * @param {*} localizationData
 */
const fullMonths = (translate, localizationData) => {
  let months = [];
  if (localizationData && localizationData._months && Array.isArray(localizationData._months)) {
    // eslint-disable-next-line prefer-destructuring
    months = localizationData._months;
  } else if (
    localizationData &&
    localizationData._months &&
    localizationData._months.standalone &&
    Array.isArray(localizationData._months.standalone)
  ) {
    months = localizationData._months.standalone;
  }

  const translatedMonths = MONTHS.map((month, index) => {
    return getMonthTranslation(
      months,
      index,
      translate,
      `month-${month}`,
      capitalizeFirstLetter(month)
    );
  });
  return translatedMonths;
};
/**
 * Given a month name gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "Enero"
 * else => labels["month-short-january"]
 * @param {*} translate
 * @param {*} localizationData
 * */
const shortMonths = (translate, localizationData) => {
  let months = [];
  if (localizationData) {
    if (typeof localizationData._monthsShort === 'function') {
      months = localizationData._monthsShort();
    } else if (localizationData._monthsShort) {
      months = localizationData._monthsShort;
    }
    if (typeof localizationData.monthsShort === 'function') {
      months = localizationData.monthsShort();
    } else if (localizationData.monthsShort) {
      months = localizationData.monthsShort;
    }
  }
  let monthsShorts = [];
  if (months && Array.isArray(months)) {
    // eslint-disable-next-line prefer-destructuring
    monthsShorts = months;
  } else if (months && months.standalone && Array.isArray(months.standalone)) {
    monthsShorts = months.standalone;
  }

  const translatedMonths = MONTHS.map((month, index) => {
    return getMonthTranslation(
      monthsShorts,
      index,
      translate,
      `month-short-${month}`,
      capitalizeFirstLetter(month.substr(0, 3))
    );
  });
  return translatedMonths;
};

/**
 * Given a weekday array gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "D" (Domingo)
 * else => labels["day-one-letter-sunday"]
 * @param {*} translate
 * @param {*} localizationData
 */
const weekDaysOneLetter = (translate, localizationData) => {
  // FIXME This is not taking into account that the first day of the week
  // can change per locale
  const weekdaysMin = localizationData ? localizationData._weekdaysMin : [];
  const translatedWeekDays = WEEKDAYS.map((weekDay, index) => {
    return getWeekDayTranslation(
      weekdaysMin,
      index,
      translate,
      `day-one-letter-${weekDay}`,
      capitalizeFirstLetter(weekDay.substr(0, 1))
    );
  });
  return translatedWeekDays;
};

/**
 * Given a weekday array gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "D" (Domingo)
 * else => labels["day-min-sunday"]
 * @param {*} translate
 * @param {*} localizationData
 */
const weekDaysMin = (translate, localizationData) => {
  // FIXME This is not taking into account that the first day of the week
  // can change per locale
  const weekdaysMin = localizationData ? localizationData._weekdaysMin : [];
  const translatedWeekDays = WEEKDAYS.map((weekDay, index) => {
    return getWeekDayMinTranslation(weekdaysMin, index, translate, `day-min-${weekDay}`, weekDay);
  });
  return translatedWeekDays;
};

/**
 * Given a weekday array gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "Do" (Domingo)
 * else => labels["day-short-letter-sunday"]
 * @param {*} translate
 * @param {*} localizationData
 */
const weekDaysShort = (translate, localizationData) => {
  const weekdaysShort = localizationData ? localizationData._weekdaysShort : [];
  const translatedWeekDays = WEEKDAYS.map((weekDay, index) => {
    return getWeekDaysShortTranslation(
      weekdaysShort,
      index,
      translate,
      `day-short-${weekDay}`,
      capitalizeFirstLetter(weekDay.substr(0, 2))
    );
  });
  return translatedWeekDays;
};

/**
 * Given a weekday array gets the localization name
 * if is defined or a label translation for it
 * if moment["ES"] => return "D" (Domingo)
 * else => labels["day-long-sunday"]
 * @param {*} translate
 * @param {*} localizationData
 */
const weekDaysLong = (translate, localizationData = undefined) => {
  let weekdays = [];
  if (localizationData && localizationData._weekdays && Array.isArray(localizationData._weekdays)) {
    // eslint-disable-next-line prefer-destructuring
    weekdays = localizationData._weekdays;
  } else if (
    localizationData &&
    localizationData._weekdays &&
    Array.isArray(localizationData._weekdays.standalone)
  ) {
    weekdays = localizationData._weekdays.standalone;
  }
  const translatedWeekDays = WEEKDAYS.map((weekDay, index) => {
    return getWeekDaysLongTranslation(
      weekdays,
      index,
      translate,
      `day-long-${weekDay}`,
      capitalizeFirstLetter(weekDay)
    );
  });
  return translatedWeekDays;
};

/**
 * Gets localizated current month
 * @param {dayjs.Dayjs} day
 * @param {any} translate
 */
export function getMonth(day, translate, localizationData, short = false) {
  const monthsArray = !short
    ? fullMonths(translate, localizationData)
    : shortMonths(translate, localizationData);
  const month = day.month();
  const year = day.year();
  return {
    name: monthsArray[month],
    number: month, // Deprecated. Should use month and year
    month,
    year
  };
}

/**
 * FIXME: Please comment me
 * @param {*} monthsNumber
 * @param {*} translate
 * @param {*} localizationData
 * @param {*} short
 * @param {*} start
 */
const getAvailableMonths = (
  monthsNumber,
  translate,
  localizationData,
  short = false,
  start = undefined
) => {
  const startMonth = start || dayjs().startOf('month');
  const monthsArray = !short
    ? fullMonths(translate, localizationData)
    : shortMonths(translate, localizationData);
  const currentMonth = startMonth.month();
  const currentYear = startMonth.year();
  const result = new Array(monthsNumber).fill(undefined).map((_el, index) => {
    const month = (index + currentMonth) % 12;
    const yearIndex = Math.floor((index + currentMonth) / 12);
    const year = currentYear + yearIndex;
    return {
      name: monthsArray[month],
      number: month, // Deprecated. Should use month and year
      month,
      year
    };
  });
  return result;
};

/**
 * Gets weekday names
 * @param {String} weekDayFormat
 * @param {*} translate
 * @param {*} localizationData
 */
const getWeekDaysName = (weekDayFormat, translate, localizationData = undefined) => {
  switch (weekDayFormat) {
    case 'long':
      return weekDaysLong(translate, localizationData);
    case 'short':
      return weekDaysShort(translate, localizationData);
    case 'min':
      return weekDaysMin(translate, localizationData);
    case 'oneLetter':
      return weekDaysOneLetter(translate, localizationData);
    default:
      return null;
  }
};

/**
 * Gets weekday names
 * @param {*} weekday
 * @param {*} weekDayFormat
 * @param {*} translate
 * @param {*} localizationData
 */
const getWeekDayName = (weekday, weekDayFormat, translate, localizationData = undefined) => {
  return getWeekDaysName(weekDayFormat, translate, localizationData)[weekday];
};

/**
 * Given a number (int) of days calculate the current dates
 * 0 = Today
 * 7 = Today + 7 days
 * @param {*} days
 * @param {*} format
 */
const getDateFromDays = (days, format) => {
  if (format) {
    return dayjs().add(days, 'day').format(format);
  }
  return dayjs().add(days, 'day');
};

/**
 * Determine if a range is valid (could be numbers or strings, ie: 12 and "13")
 * @param {Object} param
 */
const isValidRangeNumbers = ({ start, end }) =>
  ((typeof start === 'number' && typeof end === 'number') ||
    (typeof start === 'string' && typeof end === 'string')) &&
  Number(start) !== Number.NaN &&
  Number(end) !== Number.NaN &&
  Number(start) <= Number(end);

/**
 * Determine if a range of dates is valid
 * @param {Object} param
 */
const isValidRangeDates = ({ start, end }) =>
  (start &&
    end &&
    dayjs(start).isValid() &&
    dayjs(end).isValid() &&
    (dayjs(start).isBefore(dayjs(end)) || dayjs(start).isSame(dayjs(end)))) ||
  (start && dayjs(start).isValid()) ||
  (end && dayjs(end).isValid());

/**
 * Utility function that allows to calculate the start and end dates
 * @param {Object} range
 * @param {String} format
 * @param {String} scope
 */
const getDateRangeAsDates = (range, format, scope) => {
  const rangeAsDates = {
    start: dayjs().format(format),
    end: dayjs().add(45, 'day').format(format)
  };
  if (range) {
    if (isValidRangeNumbers({ ...range })) {
      rangeAsDates.start = dayjs().add(range.start, 'day').format(format);
      rangeAsDates.end = dayjs().add(range.end, 'day').format(format);
    } else if (isValidRangeDates({ ...range })) {
      rangeAsDates.start = range.start && dayjs(range.start).format(format);
      rangeAsDates.end = range.end && dayjs(range.end).format(format);
    } else {
      logger.error(
        `${scope}: **departure.start** and **departure.end** invalid entries. Please check out prepopulation values`
      );
    }
  } else {
    logger.warn(
      `${scope}: no **range** provided taking it form default: (start: today, end: today + 45 days)`
    );
  }
  return rangeAsDates;
};

export const formatAsDate = (elem, format = 'YYYY-MM-DD') => {
  return typeof elem === 'string' ||
    typeof elem === 'number' ||
    (typeof elem === 'object' && elem !== null)
    ? dayjs(elem).format(format)
    : elem;
};

const __getRelativeDateFromLocalization = (localizationData, element, format) => {
  if (
    localizationData &&
    localizationData._relativeTime &&
    localizationData._relativeTime[element]
  ) {
    if (typeof localizationData._relativeTime[element] === 'function') {
      const prexfix = element === 'future' || element === 'past' ? '%s' : '%d';
      try {
        return localizationData._relativeTime[element](prexfix, true, format);
      } catch (error) {
        return '';
      }
    }
    if (typeof localizationData._relativeTime[element] === 'string') {
      return localizationData._relativeTime[element];
    }
  }
  return '';
};

/**
 *
 * This fix is because EM in your cms adds labels and not regular
 * expressions to overwrite the locale of moment, causing the returned
 * translated text to not have `%d or %s` so that dayjs will overwrite the
 * value of the parsed date.
 * This function replace the labels translated in the moment format
 * @param {*} format '%d months'
 * @param {*} stringReplace 'meses'
 */
const replaceLabelWithFormat = (format, stringReplace) => {
  if (!format.includes(' ')) {
    if (format.includes('%d')) {
      if (format.indexOf('%d') === 0) {
        return `%d${stringReplace}`;
      } else {
        return `${stringReplace}%d`;
      }
    }
    if (format.includes('%s')) {
      if (format.indexOf('%s') === 0) {
        return `%s${stringReplace}`;
      } else {
        return `${stringReplace}%s`;
      }
    }
  } else {
    const formatParsed = format.split(' ').map((item) => {
      if (item === '%d' || item === '%s') {
        return item;
      } else {
        return stringReplace;
      }
    });
    return formatParsed.join(' ');
  }
};

/**
 * Returns a localized relativeDate object
 * currently following momentJS API https://momentjs.com/docs/
 * @param {*} translate
 * @param {*} localizationData
 */
const getRelativeDateLocalization = (translate, localizationData) => {
  const d = __getRelativeDateFromLocalization(localizationData, 'd', 'dd') || 'a day';
  const dd = __getRelativeDateFromLocalization(localizationData, 'dd', 'dd') || '%d days';
  const future = __getRelativeDateFromLocalization(localizationData, 'future') || 'in %s';
  const h = __getRelativeDateFromLocalization(localizationData, 'h', 'h') || 'an hour';
  const hh = __getRelativeDateFromLocalization(localizationData, 'hh', 'hh') || '%d hours';
  const m = __getRelativeDateFromLocalization(localizationData, 'm', 'm') || 'a min';
  const M = __getRelativeDateFromLocalization(localizationData, 'M', 'M') || 'a month';
  const mm = __getRelativeDateFromLocalization(localizationData, 'mm', 'mm') || '%d mins';
  const MM = __getRelativeDateFromLocalization(localizationData, 'MM', 'MM') || '%d months';
  const past = __getRelativeDateFromLocalization(localizationData, 'past') || '%s ago';
  const s = __getRelativeDateFromLocalization(localizationData, 's') || 'a few sec';
  const y = __getRelativeDateFromLocalization(localizationData, 'y', 'y') || 'a year';
  const yy = __getRelativeDateFromLocalization(localizationData, 'yy', 'yy') || '%d years';

  const rt = {
    d: translate(`${labelCommonKey}d`, d),
    dd:
      translate(`${labelCommonKey}dd`) === `${labelCommonKey}dd`
        ? dd
        : replaceLabelWithFormat(dd, translate(`${labelCommonKey}dd`, 'days')),
    future:
      translate(`${labelCommonKey}future`) === `${labelCommonKey}future`
        ? future
        : replaceLabelWithFormat(future, translate(`${labelCommonKey}future`, 'in')),
    h: translate(`${labelCommonKey}h`, h),
    hh:
      translate(`${labelCommonKey}hh`) === `${labelCommonKey}hh`
        ? hh
        : replaceLabelWithFormat(hh, translate(`${labelCommonKey}hh`, 'hours')),
    m: translate(`${labelCommonKey}m`, m),
    M: translate(`${labelCommonKey}M`, M),
    mm:
      translate(`${labelCommonKey}mm`) === `${labelCommonKey}mm`
        ? mm
        : replaceLabelWithFormat(mm, translate(`${labelCommonKey}mm`, 'mins')),
    MM:
      translate(`${labelCommonKey}MM`) === `${labelCommonKey}MM`
        ? MM
        : replaceLabelWithFormat(MM, translate(`${labelCommonKey}MM`, 'months')),
    past:
      translate(`${labelCommonKey}past`) === `${labelCommonKey}past`
        ? past
        : replaceLabelWithFormat(past, translate(`${labelCommonKey}past`, 'ago')),
    s: translate(`${labelCommonKey}s`, s),
    y: translate(`${labelCommonKey}y`, y),
    yy:
      translate(`${labelCommonKey}yy`) === `${labelCommonKey}yy`
        ? yy
        : replaceLabelWithFormat(yy, translate(`${labelCommonKey}yy`, 'years'))
  };

  let name = 'en';
  let LT = 'h:mm A',
    LTS = 'h:mm:ss A',
    L = 'YYYY-MM-DD',
    LL = 'MMMM D, YYYY',
    LLL = 'MMMM D, YYYY h:mm A',
    LLLL = 'dddd, MMMM D, YYYY h:mm A';
  if (localizationData) {
    if (localizationData.name || localizationData._abbr) {
      name = localizationData.name || localizationData._abbr;
    }
    if (localizationData.formats) {
      LT = localizationData.formats.LT;
      LTS = localizationData.formats.LTS;
      L = localizationData.formats.L;
      LL = localizationData.formats.LL;
      LLL = localizationData.formats.LLL;
      LLLL = localizationData.formats.LLLL;
    }
  }
  return {
    weekdays: weekDaysLong(translate, localizationData),
    weekdaysShort: weekDaysShort(translate, localizationData),
    weekdaysMin: weekDaysOneLetter(translate, localizationData),
    months: fullMonths(translate, localizationData),
    monthsShort: shortMonths(translate, localizationData),
    name,
    formats: {
      LTS:
        translate(`${labelCommonKey}lts`) === `${labelCommonKey}lts`
          ? LTS
          : translate(`${labelCommonKey}lts`),
      LT:
        translate(`${labelCommonKey}lt`) === `${labelCommonKey}lt`
          ? LT
          : translate(`${labelCommonKey}lt`),
      L:
        translate(`${labelCommonKey}l`) === `${labelCommonKey}l`
          ? L
          : translate(`${labelCommonKey}l`),
      LL:
        translate(`${labelCommonKey}ll`) === `${labelCommonKey}ll`
          ? LL
          : translate(`${labelCommonKey}ll`),
      LLL:
        translate(`${labelCommonKey}lll`) === `${labelCommonKey}lll`
          ? LLL
          : translate(`${labelCommonKey}lll`),
      LLLL:
        translate(`${labelCommonKey}llll`) === `${labelCommonKey}llll`
          ? LLLL
          : translate(`${labelCommonKey}llll`)
    },
    relativeTime: { ...rt }
  };
};

export {
  fullMonths,
  weekDaysOneLetter,
  weekDaysShort,
  weekDaysLong,
  getAvailableMonths,
  shortMonths,
  getWeekDayName,
  getWeekDaysName,
  getDateFromDays,
  getDateRangeAsDates,
  getRelativeDateLocalization
};
